import { api } from '../api/client';
import { uploadBlob, type BlobUploadProgress } from '../api/blobUpload';
import { uploadInfoShareLabel } from './shareLabelUpload';
import {
  buildFragment,
  createShareSecrets,
  encryptManifest,
} from './infoShareFragment';
import { encryptInfoShareAttachments } from './infoShareAttachments';
import {
  buildGuestPasswordVerifier,
  createGuestPasswordSalt,
  deriveInfoShareContentKey,
  encodePasswordVerifierBase64,
} from './guestPassword';
import {
  generateInfoShareReplyKeyPair,
  persistInfoShareReplyPrivateKey,
} from './infoShareReplyKeys';
import { encodeBase64Url } from './primitives';
import { upsertShareFromSummary } from '../ws/infoShareLive';

import { generateSmsPassword } from './smsPassword';
import {
  buildSmsDestinationCiphertext,
  createSmsDeliveryReference,
} from './smsDeliveryClient';
import { resolvePasswordPolicy } from './passwordPoliciesApi';
import { assertPasswordPolicy } from './passwordPolicy';

const BYTES_PER_MB = 1024 * 1024;

export type InfoSharePasswordMode = 'None' | 'UserSet' | 'Sms';

export async function createTextInfoShare(input: {
  text: string;
  label?: string;
  files?: File[];
  expiresAt: Date;
  multiUseAccess: boolean;
  allowGuestReply: boolean;
  replyAllowsFiles: boolean;
  maxReplyFiles?: number;
  maxReplyTotalBytes?: number;
  userId: string;
  passwordMode?: InfoSharePasswordMode;
  password?: string;
  smsPhoneNumber?: string;
  onProgress?: (progress: BlobUploadProgress) => void;
  /** Fired once the SMS tracking reference is known, before the create API call. May wait for live WS. */
  onSmsDeliveryReferenceReady?: (reference: string) => void | Promise<void>;
}): Promise<{ guestUrl: string; shareId: string; smsDeliveryReference?: string }> {
  const secrets = createShareSecrets();
  const passwordMode = input.passwordMode ?? 'None';
  const userPassword = input.password?.trim() ?? '';

  let contentKey = secrets.contentKey;
  let passwordVerifier: string | undefined;
  let fragmentSalt: string | undefined;
  let smsPassword: string | undefined;
  let smsPhoneNumber: string | undefined;
  let smsDeliveryReference: string | undefined;
  let smsDestinationCiphertext: string | undefined;

  if (passwordMode === 'UserSet') {
    const policy = await resolvePasswordPolicy('infoSharePassword');
    assertPasswordPolicy(userPassword, policy);

    const salt = createGuestPasswordSalt();
    contentKey = await deriveInfoShareContentKey(userPassword, salt);
    passwordVerifier = encodePasswordVerifierBase64(
      await buildGuestPasswordVerifier(userPassword, salt),
    );
    fragmentSalt = encodeBase64Url(salt);
  } else if (passwordMode === 'Sms') {
    const generated = generateSmsPassword();
    const salt = createGuestPasswordSalt();
    contentKey = await deriveInfoShareContentKey(generated.digits, salt);
    passwordVerifier = encodePasswordVerifierBase64(
      await buildGuestPasswordVerifier(generated.digits, salt),
    );
    fragmentSalt = encodeBase64Url(salt);
    smsPassword = generated.digits;
    smsPhoneNumber = input.smsPhoneNumber?.trim();
    if (!smsPhoneNumber) {
      throw new Error('sms_phone_required');
    }
    smsDeliveryReference = createSmsDeliveryReference();
    smsDestinationCiphertext = await buildSmsDestinationCiphertext(
      smsPhoneNumber,
      smsDeliveryReference,
    );
  }

  const files = input.files ?? [];
  const encryptedFiles = await encryptInfoShareAttachments(contentKey, files, input.onProgress);
  const manifestBlob = await encryptManifest(contentKey, {
    text: input.text,
    attachments: encryptedFiles.attachments,
  });
  const manifestBlobId = await uploadBlob(manifestBlob);

  const fragment = buildFragment({
    v: 1,
    fs: encodeBase64Url(secrets.proofSeed),
    ...(passwordMode === 'None'
      ? { ck: encodeBase64Url(secrets.contentKey) }
      : { salt: fragmentSalt }),
    mb: encodeBase64Url(new TextEncoder().encode(manifestBlobId)),
  });

  let replyEncryptionPublicKey: Uint8Array | undefined;
  let replyPrivateKey: Uint8Array | undefined;

  if (input.allowGuestReply) {
    const keys = generateInfoShareReplyKeyPair();
    replyEncryptionPublicKey = keys.publicKey;
    replyPrivateKey = keys.privateKey;
  }

  // Open live WS immediately before the create API (SMS send) so DLRs are not missed,
  // but after client-side crypto/upload so list sync cannot tear the socket down mid-prepare.
  if (smsDeliveryReference) {
    await input.onSmsDeliveryReferenceReady?.(smsDeliveryReference);
  }

  const created = await api.createInfoShare({
    manifestBlobId,
    fragmentProofPublicKey: secrets.proofPublicKey,
    expiresAt: input.expiresAt.toISOString(),
    multiUseAccess: input.multiUseAccess,
    passwordMode,
    passwordVerifier,
    smsPhoneNumber,
    smsPassword,
    smsDeliveryReference,
    smsDestinationCiphertext,
    allowGuestReply: input.allowGuestReply,
    replyAllowsFiles: input.allowGuestReply && input.replyAllowsFiles,
    replyRetentionDaysAfterOpen: 7,
    maxReplyFiles: input.allowGuestReply && input.replyAllowsFiles ? input.maxReplyFiles : undefined,
    maxReplyTotalBytes:
      input.allowGuestReply && input.replyAllowsFiles ? input.maxReplyTotalBytes : undefined,
    replyEncryptionPublicKey,
    attachmentBlobIds: encryptedFiles.attachmentBlobIds.length > 0
      ? encryptedFiles.attachmentBlobIds
      : undefined,
  });

  if (replyPrivateKey) {
    await persistInfoShareReplyPrivateKey(created.id, replyPrivateKey, input.userId);
  }

  let labelBlobId: string | null = null;
  if (input.label?.trim()) {
    labelBlobId = await uploadInfoShareLabel(created.id, input.label);
    await api.patchInfoShare(created.id, { labelBlobId });
  }

  const trackingReference = created.smsDeliveryReference ?? smsDeliveryReference;
  upsertShareFromSummary({
    id: created.id,
    serverToken: created.serverToken,
    displayStatus: 'Pending',
    createdAt: new Date().toISOString(),
    expiresAt: created.expiresAt,
    multiUseAccess: input.multiUseAccess,
    allowGuestReply: input.allowGuestReply,
    passwordMode,
    unreadReplyCount: 0,
    labelBlobId,
    wasOpened: false,
    smsDeliveryReference: trackingReference ?? null,
    smsDeliveryStatus: null,
    smsDeliveryErrorDescription: null,
  });

  const guestUrl = `${window.location.origin}${created.guestPath}#${fragment}`;
  return {
    guestUrl,
    shareId: created.id,
    smsDeliveryReference: trackingReference,
  };
}

export function parseReplyOptionsFromForm(form: HTMLFormElement): {
  allowGuestReply: boolean;
  replyAllowsFiles: boolean;
  maxReplyFiles?: number;
  maxReplyTotalBytes?: number;
} {
  const data = new FormData(form);
  const allowGuestReply = data.get('allowGuestReply') === 'on';
  const replyAllowsFiles = allowGuestReply && data.get('replyAllowsFiles') === 'on';

  if (!allowGuestReply || !replyAllowsFiles) {
    return { allowGuestReply, replyAllowsFiles: false };
  }

  const maxReplyFiles = Number.parseInt(String(data.get('maxReplyFiles') ?? ''), 10);
  const maxReplyMb = Number.parseInt(String(data.get('maxReplyTotalMb') ?? ''), 10);

  return {
    allowGuestReply,
    replyAllowsFiles,
    maxReplyFiles: Number.isFinite(maxReplyFiles) && maxReplyFiles > 0 ? maxReplyFiles : undefined,
    maxReplyTotalBytes:
      Number.isFinite(maxReplyMb) && maxReplyMb > 0 ? maxReplyMb * BYTES_PER_MB : undefined,
  };
}
